import { useState } from "react";
import {
  Footnote,
  Dialog,
  FlexRow,
  Button,
  useChameleonTheme,
} from "@petsapp/chameleon";
import styled from "styled-components";
import { BasePlanView } from "./components/plan";
import { EntitlementsDialog } from "./components/entitlements-dialog";
import { Doodle } from "./assets";
import { useTranslation } from "../../utils/i18n";
import { parseTerms } from "../../utils/parse-terms-conditions";
import useWindowSize, { Size } from "../../hooks/use-window-size";
import { MOBILE_BREAKPOINT_PX } from "../../utils/styles";
import { EVENTS } from "../authentication/events";
import { useAnalytics } from "@petsapp/use-analytics";
import { WellnessPlan } from "./types";

const PlanContainer = styled.div`
  background-color: white;
  padding: 32px;
  border-radius: 12px;
  margin-bottom: 24px;
`;

const DoodleIcon = styled(Doodle)`
  position: absolute;
  z-index: 0;
  bottom: 0px;
  right: -518px;
`;

const IconContainer = styled.div`
  position: absolute;
  display: none;

  @media (min-width: 1200px) {
    display: block;
  }
`;

export function WellnessLandingList({ plans }: { plans: WellnessPlan[] }) {
  const { t, i18n } = useTranslation();
  // @ts-ignore
  const { chameleon } = useChameleonTheme();

  const windowSize: Size = useWindowSize();
  const renderLargeLanding = windowSize.width > MOBILE_BREAKPOINT_PX;
  const [showAllPlans, setShowAllPlans] = useState(renderLargeLanding);
  const [showTerms, setShowTerms] = useState<string | null>(null);
  const [showDialog, setShowDialog] = useState<string>("");
  const { wrap } = useAnalytics();

  const renderablePlans = showAllPlans ? plans : [plans[0]];

  return (
    <div style={{ margin: "24px", flex: "1", maxWidth: "490px" }}>
      <IconContainer>
        <DoodleIcon />
      </IconContainer>
      {renderablePlans.map((plan) => {
        const { name, price, description, entitlements, wellnessPlanId } = plan;
        const parsedTermsAndConditions = parseTerms(plan?.termsAndConditions);
        const formattedPrice = Intl.NumberFormat(i18n.language, {
          style: "currency",
          currency: plan.currency,
        }).format(price / 100);

        return (
          <PlanContainer>
            <BasePlanView
              name={name}
              price={formattedPrice}
              description={description}
              duration="month"
              pricePrefix={renderLargeLanding ? t("From") : ""}
            />
            <>
              <FlexRow style={{ marginTop: "1rem" }}>
                <Button
                  style={{ flex: "1" }}
                  variant="secondary"
                  onClick={wrap(
                    () => setShowDialog(wellnessPlanId),
                    EVENTS.viewEntitlementsOnWellnessLanding,
                    {
                      name,
                      price,
                    },
                  )}
                >
                  {t("View entitlements")}
                </Button>
              </FlexRow>
              <EntitlementsDialog
                heading={`${t(
                  "For 12 months from the day you join you'll get",
                )}:`}
                formattedPrice={formattedPrice}
                name={name}
                description={description}
                entitlements={entitlements}
                priceRange={t("per month")}
                showDialog={showDialog === wellnessPlanId}
                onCancelDialog={() => setShowDialog("")}
              />
              {!renderLargeLanding && (
                <>
                  <Footnote
                    style={{
                      cursor: "pointer",
                      color: chameleon.color.Primary,
                    }}
                    onClick={wrap(
                      () =>
                        setShowTerms(
                          (parsedTermsAndConditions as unknown) as string,
                        ),
                      EVENTS.viewTermsAndConditionsOnWellnessLanding,
                      {
                        name,
                        price,
                      },
                    )}
                  >
                    {t("wellness:Terms and conditions apply")}
                  </Footnote>
                </>
              )}
            </>
          </PlanContainer>
        );
      })}
      {!showAllPlans && (
        <Button
          style={{ width: "100%" }}
          variant="secondary"
          onClick={() => setShowAllPlans((p) => !p)}
        >
          Show all plans
        </Button>
      )}
      <Dialog
        open={!!showTerms}
        onCancel={() => setShowTerms(null)}
        title={t("wellness:Terms and conditions")}
        content={() => <FlexRow>{showTerms}</FlexRow>}
      />
    </div>
  );
}
